<template>
  <div>
    <ErrorPage v-if="error404occured" />
    <div v-else>
      <div v-show="apiInfoLoading">
        <div class="container my-4 pl-0">
          <div aria-label="breadcrumb">
            <ol class="breadcrumb py-0 my-0">
              <li class="breadcrumb-item">
                <router-link :to="{ name: 'home', params: { lang: $route.params.lang }}">{{ $t('CC01-03-08') }}</router-link>
              </li>
              <li class="breadcrumb-item py-0 my-0">
                <router-link :to="{ name: 'api-catalogue', params: { category: 'all', lang: $route.params.lang }}">{{ $t('M06a-03') }}</router-link>
              </li>
              <li class="breadcrumb-item py-0 my-0"><SkeletonUI type="span" class="skeleton-route" /></li>
              <li class="breadcrumb-item py-0 my-0 active" aria-current="page"><SkeletonUI type="span" class="skeleton-route" /></li>
            </ol>
          </div>
        </div>
        <div class="gradient-y-gray">
          <div class="container space-1">
            <div class="media">
              <div class="avatar mr-5">
                <SkeletonUI type="img" class="skeleton-image" />
              </div>
              <div class="media-body">
                <SkeletonUI type="button" />
                <SkeletonUI type="h2" class="my-2" />
                <SkeletonUI type="p" class="skeleton-date" />
              </div>
            </div>
          </div>
          <div v-if="activeTab === 'unlisted-pricing'">
            <div class="container pl-0">
              <div aria-label="breadcrumb">
                <ol class="breadcrumb my-0 py-0">
                  <li class="breadcrumb-item">
                    <router-link
                      :to="{
                        name: 'api-info',
                        params: {
                          provider_name: $route.params.provider_name,
                          api_name: $route.params.api_name,
                          tab_name: 'pricing'
                        },
                        query: $route.query
                      }"
                    >
                      {{ $t('B-M08b-07c') }}
                    </router-link>
                  </li>
                  <li class="breadcrumb-item active" aria-current="page"><a class="disabled">{{ `${$t(plan_info.show_name)}${plan_info.accessibility === 'public' ? `` : ` - ${$t('M08ci-02-01')}`}` }}</a></li>
                </ol>
              </div>
              <div class="alert alert-soft-primary unlisted-info-box">
                <div class="d-flex">
                  <div class="flex-shrink-0">
                    <i class="fas fa-info-circle mr-2" />
                  </div>
                  <div>{{ $t('M08ci-03') }}</div>
                </div>
              </div>
            </div>
          </div>
          <!-- Nav -->
          <div v-else class="container">
            <div class="js-nav-scroller hs-nav-scroller-horizontal">
              <span class="hs-nav-scroller-arrow-prev d-none">
                <a class="hs-nav-scroller-arrow-link">
                  <i class="fa fa-chevron-left" />
                </a>
              </span>

              <span class="hs-nav-scroller-arrow-next d-none">
                <a class="hs-nav-scroller-arrow-link">
                  <i class="fa fa-chevron-right" />
                </a>
              </span>

              <ul class="nav nav-tabs">
                <li
                  id="ga-B-M08b-07b"
                  class="nav-item"
                  @click="switchTab('readme')"
                >
                  <a
                    class="nav-link cursor-pointer"
                    :class="{ 'active': activeTab === 'readme' }"
                  >{{ $t('B-M08b-07b') }}</a>
                </li>
                <li
                  id="ga-B-M08b-07a"
                  class="nav-item"
                  @click="switchTab('endpoint')"
                >
                  <a
                    class="nav-link cursor-pointer"
                    :class="{ 'active': activeTab === 'endpoint' }"
                  >{{ $t('B-M08b-07a') }}</a>
                </li>
                <li
                  v-if="api_info[0] && api_info[0].is_demo_enabled"
                  id="ga-B-M08b-07d"
                  class="nav-item"
                  @click="switchTab('demo')"
                >
                  <a
                    class="nav-link cursor-pointer"
                    :class="{ 'active': activeTab === 'demo' }"
                  >{{ $t('B-M08b-07d') }}</a>
                </li>
                <li
                  v-if="api_info[0] && (plans.length > 0 || api_info[0].is_custom_plan_supported)"
                  id="ga-B-M08b-07c"
                  class="nav-item"
                  @click="switchTab('pricing')"
                >
                  <a
                    class="nav-link cursor-pointer"
                    :class="{ 'active': activeTab === 'pricing' }"
                  >{{ $t('B-M08b-07c') }}</a>
                </li>
              </ul>
            </div>
          </div>
          <!-- End Nav -->
        </div>
      </div>
      <div v-show="!apiInfoLoading">
        <div v-for="item in api_info" :key="item.api_id">
          <div class="container my-4 pl-0">
            <div aria-label="breadcrumb">
              <ol class="breadcrumb py-0 my-0">
                <li class="breadcrumb-item">
                  <router-link :to="{ name: 'home', params: { lang: $route.params.lang }}">{{ $t('CC01-03-08') }}</router-link>
                </li>
                <li class="breadcrumb-item py-0 my-0">
                  <router-link :to="{ name: 'api-catalogue', params: { category: 'all', lang: $route.params.lang }}">{{ $t('M06a-03') }}</router-link>
                </li>
                <li class="breadcrumb-item py-0 my-0">
                  <router-link
                    :to="{
                      name: 'api-catalogue',
                      params: {
                        category: getCategoryFormattedName(item.api_category),
                        lang: $route.params.lang
                      }
                    }"
                  >{{ getCategoryName(item.api_category) }}</router-link>
                </li>
                <li class="breadcrumb-item py-0 my-0 active" aria-current="page">
                  <a class="disabled">{{ item.api_name }}</a>
                </li>
              </ol>
            </div>
          </div>

          <div id="stickyBlockStartPoint" class="col-lg-2 pr-xl-5 mb-5 mb-lg-0">
            <!-- Filter -->
            <div
              class="js-sticky-block"
              data-hs-sticky-block-options="{
              &quot;parentSelector&quot;: &quot;#stickyBlockStartPoint&quot;,
              &quot;breakpoint&quot;: &quot;lg&quot;,
              &quot;startPoint&quot;: &quot;#stickyBlockStartPoint&quot;,
              &quot;endPoint&quot;: &quot;#stickyBlockEndPoint&quot;,
              &quot;stickyOffsetTop&quot;: 16
              }"
            />
          </div>
          <div id="stickyBlockEndPoint" />

          <div class="gradient-y-gray">
            <div class="container space-1">
              <div class="media">
                <div class="avatar mr-5">
                  <img v-img class="avatar-img shadow" :src="(provider_info[0] && provider_info[0].theme_data && provider_info[0].theme_data.profile_picture_url) ? provider_info[0].theme_data.profile_picture_url : require('@/assets/developer-icon/500x500-01.jpg')" alt="Image Description">
                </div>
                <div class="media-body">
                  <span>
                    <router-link :to="`/api-catalogue/${getCategoryFormattedName(item.api_category)}`" class="btn btn-xs btn-primary mb-2 category">
                      {{ getCategoryName(item.api_category) }}
                    </router-link>
                  </span>
                  <span v-for="api_group in item.api_provider_group" :key="api_group" class="btn btn-xs btn-soft-secondary mb-2 btn-disabled api-group">{{ api_group }}</span>
                  <div class="d-flex align-items-center justify-content-between">
                    <h1 class="h2 mb-2">{{ item.api_name }}</h1>
                    <div>
                      <div
                        v-if="isSubscribeButtonShown"
                        class="theme-button btn btn-primary theme-button-desktop"
                        :class="{ 'mr-4': item.swagger_file && activeTab === 'endpoint' }"
                        @click="switchTab('pricing')"
                      >
                        {{ $t('B-M08b-36') }}
                      </div>
                      <a v-if="item.swagger_file && activeTab === 'endpoint'" :href="getSwaggerDownloadLink()" :download="`${item.api_name}.json`" class="theme-outline-button btn btn-outline-primary theme-outline-button-desktop">
                        <i class="fas fa-arrow-to-bottom" /> {{ $t('B-M08b-35') }}
                      </a>
                    </div>
                  </div>
                  <p>
                    {{ $t('M-M08b-07a') }}
                    <router-link
                      :to="{
                        name: 'provider-info',
                        params: {
                          provider_name: provider_info[0] && provider_info[0].display_name.toLowerCase().replace(/[^A-Za-z0-9]/g, '-').concat(`-${item.provider_short_id}`).replace(/\-+/g, '-'),
                          tab_name: 'home',
                          lang: $route.params.lang
                        }
                      }"
                    >
                      {{ provider_info[0] && provider_info[0].display_name }}
                    </router-link>{{ $t('M-M08b-07b') }} | {{ $t('B-M08b-06') }} {{ item.api_last_update_date }}</p>
                  <div>
                    <div
                      v-if="isSubscribeButtonShown"
                      class="theme-button btn btn-primary theme-button-mobile"
                      :class="{ 'mr-4': item.swagger_file && activeTab === 'endpoint' }"
                      @click="switchTab('pricing')"
                    >
                      {{ $t('B-M08b-36') }}
                    </div>
                    <a v-if="item.swagger_file && activeTab === 'endpoint'" :href="getSwaggerDownloadLink()" :download="`${item.api_name}.json`" class="theme-outline-button btn btn-outline-primary theme-outline-button-mobile">
                      <i class="fas fa-arrow-to-bottom" /> {{ $t('B-M08b-35') }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="activeTab === 'unlisted-pricing'">
              <div class="container pl-0">
                <div aria-label="breadcrumb">
                  <ol class="breadcrumb my-0 py-0">
                    <li class="breadcrumb-item">
                      <router-link
                        :to="{
                          name: 'api-info',
                          params: {
                            provider_name: $route.params.provider_name,
                            api_name: $route.params.api_name,
                            tab_name: 'pricing'
                          },
                          query: $route.query
                        }"
                      >
                        {{ $t('B-M08b-07c') }}
                      </router-link>
                    </li>
                    <li class="breadcrumb-item active" aria-current="page"><a class="disabled">{{ `${$t(plan_info.show_name)}${plan_info.accessibility === 'public' ? `` : ` - ${$t('M08ci-02-01')}`}` }}</a></li>
                  </ol>
                </div>
                <div class="alert alert-soft-primary unlisted-info-box">
                  <div class="d-flex">
                    <div class="flex-shrink-0">
                      <i class="fas fa-info-circle mr-2" />
                    </div>
                    <div>{{ $t('M08ci-03') }}</div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Nav -->
            <div v-else class="container">
              <div class="js-nav-scroller hs-nav-scroller-horizontal">
                <span class="hs-nav-scroller-arrow-prev d-none">
                  <a class="hs-nav-scroller-arrow-link">
                    <i class="fa fa-chevron-left" />
                  </a>
                </span>

                <span class="hs-nav-scroller-arrow-next d-none">
                  <a class="hs-nav-scroller-arrow-link">
                    <i class="fa fa-chevron-right" />
                  </a>
                </span>

                <ul class="nav nav-tabs">
                  <li
                    id="ga-B-M08b-07b"
                    class="nav-item"
                    @click="switchTab('readme')"
                  >
                    <a
                      class="nav-link cursor-pointer"
                      :class="{ 'active': activeTab === 'readme' }"
                    >{{ $t('B-M08b-07b') }}</a>
                  </li>
                  <li
                    id="ga-B-M08b-07a"
                    class="nav-item"
                    @click="switchTab('endpoint')"
                  >
                    <a
                      class="nav-link cursor-pointer"
                      :class="{ 'active': activeTab === 'endpoint' }"
                    >{{ $t('B-M08b-07a') }}</a>
                  </li>
                  <li
                    v-if="api_info[0] && api_info[0].is_demo_enabled"
                    id="ga-B-M08b-07d"
                    class="nav-item"
                    @click="switchTab('demo')"
                  >
                    <a
                      class="nav-link cursor-pointer"
                      :class="{ 'active': activeTab === 'demo' }"
                    >{{ $t('B-M08b-07d') }}</a>
                  </li>
                  <li
                    v-if="api_info[0] && (plans.length > 0 || api_info[0].is_custom_plan_supported)"
                    id="ga-B-M08b-07c"
                    class="nav-item"
                    @click="switchTab('pricing')"
                  >
                    <a
                      class="nav-link cursor-pointer"
                      :class="{ 'active': activeTab === 'pricing' }"
                    >{{ $t('B-M08b-07c') }}</a>
                  </li>
                </ul>
              </div>
            </div>
            <!-- End Nav -->
          </div>
        </div>
      </div>
      <div>
        <SkeletonUI v-if="activeTab === '' && loading" type="empty" class="loading-tab" />
        <ApiInfoDemo v-if="activeTab === 'demo'" :apiInfo="api_info" :themeColor="theme_color" :profileId="userProfile.id" />
        <ApiInfoEndpoints v-if="activeTab === 'endpoint'" :apiInfo="api_info" :profileId="userProfile.id" />
        <ApiMoreResponse v-if="activeTab === 'readme'" :apiInfo="api_info" :guides="guides" :lastUpdateDate="last_update_date" />
        <ApiInfoPricing v-if="activeTab === 'pricing' || activeTab === 'unlisted-pricing'" :apiInfo="api_info" :providerInfo="provider_info" :planData="plans" />
      </div>
    </div>
    <!-- ========== Go to Top ========== -->
    <a
      class="js-go-to go-to position-fixed invisible"
      href="#stickyBlockStartPoint"
      :data-hs-go-to-options="`{
          &quot;targetSelector&quot; : &quot;#logoAndNav&quot;,
          &quot;offsetTop&quot;: 200,
          &quot;position&quot;: {
            &quot;init&quot;: {
              &quot;right&quot;: 30
            },
            &quot;show&quot;: {
              &quot;bottom&quot;: ${activeTab === 'endpoint' && smallWindow ? 120 : 30}
            },
            &quot;hide&quot;: {
              &quot;bottom&quot;: ${activeTab === 'endpoint' && smallWindow ? -120 : -30}
            }
          }
        }`"
    >
      <i class="fas fa-angle-up" />
    </a>
    <!-- ========== End Go to Top ========== -->
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import HSGoTo from '@/assets/vendor/hs-go-to/src/js/hs-go-to'
import ApiInfoDemo from './components/demo'
import ApiInfoEndpoints from './components/endpoints'
import ApiInfoPricing from './components/pricing'
import ApiMoreResponse from './components/more-response'
import SkeletonUI from '@/components/skeleton-ui'
import ErrorPage from '@/views/error-page/index'
import { getAPIInfo } from '@/api/api-info'
import { getReadme } from '@/api/readme'
import { getAuthInfo, getProviderInfo } from '@/api/profile'
import { getDisplayDate } from '@/utils/date-time-util'
import { getCategoryCode, getCategoryFormattedName } from '@/utils/format-util'
import getEditableInfo from '@/utils/get-editable-info'
const $ = require('jquery')

export default {
  name: 'ApiInfo',
  components: {
    ApiInfoDemo,
    ApiInfoEndpoints,
    ApiInfoPricing,
    ApiMoreResponse,
    ErrorPage,
    SkeletonUI
  },
  data() {
    return {
      is_special_plan: false,
      api_short_id: '',
      provider_short_id: '',
      activeTab: '',
      goingToTab: '',
      api_info: [],
      provider_info: [],
      plan_info: {
        show_name: '',
        accessibility: ''
      },
      plans: [],
      guides: '',
      last_update_date: '',
      error404occured: false,
      tabList: [
        { label: 'TUTORIAL', value: 'readme', tab_name: 'Meta-M08d-tab' },
        { label: 'Documentation', value: 'endpoint', tab_name: 'Meta-M08b-tab' },
        { label: 'Live Demo', value: 'demo', tab_name: 'Meta-M08e-tab' },
        { label: 'Pricing', value: 'pricing', tab_name: 'Meta-M08c-tab' }
      ],
      apiInfoLoading: false,
      isSubscribeButtonShown: false
    }
  },
  computed: {
    loading() {
      return this.$store.getters.loading
    },
    userProfile() {
      return this.$store.getters.userProfile
    },
    smallWindow() {
      return window.innerWidth <= 768
    },
    ...mapGetters([
      'site_info'
    ])
  },
  watch: {
    async userProfile(newProfile) {
      if (newProfile.id !== '') {
        var routeParams = this.$route.params && this.$route.params.api_name.split('-')
        this.api_short_id = routeParams[routeParams.length - 1]
        await this.initialise()
      }
    },
    $route(to, from) {
      if (to.name === 'api-info-unlisted-pricing') {
        this.goingToTab = 'unlisted-pricing'
      } else {
        if (to.params && to.params.tab_name && to.params?.tab_name !== from.params?.tab_name) {
          this.goingToTab = to.params.tab_name
        }
      }
    },
    async goingToTab() {
      if (this.api_info[0] && this.api_info[0]?.api_type !== 'public') {
        const auth = await getAuthInfo().catch(e => {
          return {}
        })
        if ($.isEmptyObject(auth)) {
          const redirectURL = `${window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'))}/${this.goingToTab}`
          this.$router.push({
            name: 'login-to-continue',
            params: this.$route.params,
            query: {
              redirect: encodeURIComponent(redirectURL)
            }
          })
        } else {
          this.activeTab = this.goingToTab
        }
      } else {
        this.activeTab = this.goingToTab
      }
    },
    activeTab() {
      if (this.activeTab !== 'unlisted-pricing') {
        this.$router.push({
          name: 'api-info',
          params: {
            provider_name: this.$route.params.provider_name,
            api_name: this.$route.params.api_name,
            tab_name: this.activeTab
          },
          query: this.$route.query
        })
        if (this.api_info && this.api_info[0]) {
          var active = this.tabList.find(item => item.value === this.activeTab)
          document.title = this.$t(active.tab_name, { api_name: this.api_info[0].api_name, provider_name: this.provider_info[0].display_name, site_name: this.site_info[this.$i18n.locale].name })
          this.isSubscribeButtonShown = (this.plans.length > 0 || this.api_info[0].is_custom_plan_supported) && this.api_info[0].swagger_file && (this.activeTab !== 'pricing' && this.activeTab !== 'unlisted-pricing')
        }
      }
    },
    '$i18n.locale'() {
      var active = this.tabList.find(item => item.value === this.activeTab)
      document.title = this.$t(active.tab_name, { api_name: this.api_info[0].api_name, provider_name: this.provider_info[0].display_name, site_name: this.site_info[this.$i18n.locale].name })
    }
  },
  updated() {
    new HSGoTo($('.js-go-to')).init()
  },
  async created() {
    var routeParams = this.$route.params && this.$route.params.api_name.split('-')
    var providerRouteParams = this.$route.params.provider_name.split('-')
    this.api_short_id = routeParams[routeParams.length - 1]
    this.provider_short_id = providerRouteParams[providerRouteParams.length - 1]
    await this.initialise()
    new HSGoTo($('.js-go-to')).init()
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'api-info') {
      this.$store.dispatch('app/setShowUpgradedPortal', false)
    }
    next()
  },
  methods: {
    async initialise() {
      this.$store.dispatch('app/setLoading', true)
      await this.getAPI()
      await this.getProvider(this.provider_short_id)
      this.initialiseTab()
      this.$store.dispatch('app/setLoading', false)
    },
    initialiseTab() {
      var active = {}
      if (this.$route.name === 'api-info-unlisted-pricing') {
        this.is_special_plan = true
        this.activeTab = this.plans.length > 0 || this.api_info[0].is_custom_plan_supported ? 'unlisted-pricing' : 'readme'
        active = this.tabList.find(item => item.value === 'pricing')
        document.title = this.$t(active.tab_name, { api_name: this.api_info[0].api_name, provider_name: this.provider_info[0].display_name, site_name: this.site_info[this.$i18n.locale].name })
      } else {
        active = this.tabList.find(item => item.value === this.$route.params.tab_name)
        if (this.$route.params.tab_name && active) {
          switch (this.$route.params.tab_name) {
            case 'demo':
              this.activeTab = this.api_info[0].is_demo_enabled ? this.$route.params.tab_name : 'readme'
              break
            case 'pricing':
              this.activeTab = this.plans.length > 0 || this.api_info[0].is_custom_plan_supported ? this.$route.params.tab_name : 'readme'
              break
            default:
              this.activeTab = this.$route.params.tab_name
              break
          }
          document.title = this.$t(active.tab_name, { api_name: this.api_info[0].api_name, provider_name: this.provider_info[0].display_name, site_name: this.site_info[this.$i18n.locale].name })
        } else {
          this.activeTab = 'readme'
        }
      }
    },
    switchTab(tab) {
      this.goingToTab = tab
    },
    async getAPI() {
      // TODO: Handle in store
      this.apiInfoLoading = true
      const plan_id = this.$route.name === 'api-info-unlisted-pricing' ? this.$route.params.plan_id : ''
      await getAPIInfo(this.userProfile.id, this.api_short_id, plan_id)
        .then(async res => {
          const response = res['data'].map(api => {
            return {
              ...api,
              api_id: api.id,
              api_short_id: api.short_id,
              api_name: api.name,
              api_type: api.accessibility,
              api_description: api.short_description,
              api_category: api.category,
              api_no_of_calls: api.request_count,
              api_errors_percentage: api.success_rate,
              api_latency: api.response_time,
              api_provider_group: api.groups,
              api_last_update_date: api.last_edit
            }
          })
          this.api_info = response

          //  TODO - uncomment this segment for mock data
          // if (this.api_info.length) {
          //   this.api_info[0] = {
          //     ...this.api_info[0],
          //     is_demo_enabled: true,
          //     demo_method: 'jsfiddle',
          //     demo_meta: '{\"path\":\"/fancs/4xv0eypk/49/embedded/result/\"}'
          //   }
          // }

          if (this.api_info.length === 0) {
            this.error404occured = true
            this.apiInfoLoading = false
            this.$store.dispatch('app/setLoading', false)
          } else {
            this.api_info = this.api_info.map(info => {
              this.last_update_date = info.api_last_update_date ? info.api_last_update_date : ''
              return {
                ...info,
                api_last_update_date: getDisplayDate(info.api_last_update_date)
              }
            })
            this.api_name = this.api_info[0]?.api_name
            this.apiInfoLoading = false
            // for Readme page
            if (this.api_info[0].readme && this.api_info[0].readme.split('.').pop() === 'md') {
              await this.getReadme()
            }
            // for Pricing page
            if (this.api_info[0].subscription_plans && this.api_info[0].subscription_plans.length > 0) {
              const subscriptionPlans = this.api_info[0].subscription_plans.map(plan => {
                return {
                  ...plan,
                  plan_version: plan.version,
                  plan_name: plan.name,
                  plan_description: plan.description,
                  plan_currency: plan.currency,
                  plan_price: plan.price,
                  plan_quota: plan.quota,
                  over_usage_charge: plan.overuse_charge
                }
              })
              const starterPlanList = subscriptionPlans.filter((planItem) => planItem.plan_name === 'Free Plan' || planItem.plan_name === 'Free' || planItem.plan_name === 'Trial' || planItem.plan_name === 'Starter')
              if (starterPlanList.length > 1) {
                starterPlanList.sort((a, b) => {
                  return a.plan_version < b.plan_version ? 1 : b.plan_version < a.plan_version ? -1 : 0
                })
              }
              var starterPlan = starterPlanList.length > 0 ? JSON.parse(JSON.stringify(starterPlanList[0])) : []
              const litePlanList = subscriptionPlans.filter((planItem) => planItem.plan_name === 'Basic' || planItem.plan_name === 'Lite')
              if (litePlanList.length > 1) {
                litePlanList.sort((a, b) => {
                  return a.plan_version < b.plan_version ? 1 : b.plan_version < a.plan_version ? -1 : 0
                })
              }
              var litePlan = litePlanList.length > 0 ? JSON.parse(JSON.stringify(litePlanList[0])) : []
              const proPlanList = subscriptionPlans.filter((planItem) => planItem.plan_name === 'Pro')
              if (proPlanList.length > 1) {
                proPlanList.sort((a, b) => {
                  return a.plan_version < b.plan_version ? 1 : b.plan_version < a.plan_version ? -1 : 0
                })
              }
              var proPlan = proPlanList.length > 0 ? JSON.parse(JSON.stringify(proPlanList[0])) : []
              const advancedPlanList = subscriptionPlans.filter((planItem) => planItem.plan_name === 'Advanced')
              if (advancedPlanList.length > 1) {
                advancedPlanList.sort((a, b) => {
                  return a.plan_version < b.plan_version ? 1 : b.plan_version < a.plan_version ? -1 : 0
                })
              }
              var advancedPlan = advancedPlanList.length > 0 ? JSON.parse(JSON.stringify(advancedPlanList[0])) : []
              const premiumPlanList = subscriptionPlans.filter((planItem) => planItem.plan_name === 'Premium')
              if (premiumPlanList.length > 1) {
                premiumPlanList.sort((a, b) => {
                  return a.plan_version < b.plan_version ? 1 : b.plan_version < a.plan_version ? -1 : 0
                })
              }
              var premiumPlan = premiumPlanList.length > 0 ? JSON.parse(JSON.stringify(premiumPlanList[0])) : []
              this.plans = [].concat(starterPlan, litePlan, proPlan, advancedPlan, premiumPlan)
              //  Handling of get last version of plan only
              /*
                    this.plans = planRes['data'].filter(r => r.api_id === this.api_info[0].api_id)
                  */
              //
              if (this.plans.length === 0) {
                var pricingIndex = this.tabList.indexOf('pricing')
                pricingIndex !== -1 ? this.tabList.splice(pricingIndex, 1) : null
              } else {
                if (this.$route.name === 'api-info-unlisted-pricing') {
                  switch (this.plans[0].name) {
                    case 'Free':
                    case 'Free Plan':
                    case 'Trial':
                    case 'Starter':
                      this.plan_info.show_name = 'M08c-05-01'
                      break
                    case 'Basic':
                    case 'Lite':
                      this.plan_info.show_name = 'M08c-05-05'
                      break
                    case 'Pro':
                      this.plan_info.show_name = 'M08c-05-02'
                      break
                    case 'Advanced':
                      this.plan_info.show_name = 'M08c-05-06'
                      break
                    case 'Premium':
                      this.plan_info.show_name = 'M08c-05-03'
                      break
                  }
                  this.plan_info.accessibility = this.plans[0].accessibility ? this.plans[0].accessibility : 'public'
                }
              }
            }
            this.initialiseTab()
          }
        })
        .catch(error => {
          console.log('getAPIInfo error', error)
          // this.error404occured = true
          this.apiInfoLoading = false
          this.$store.dispatch('app/setLoading', false)
        })
    },
    async getProvider(providerShortId) {
      await getProviderInfo(this.userProfile.id, providerShortId)
        .then(res => {
          const response = res['data'].map(provider => {
            return {
              ...provider,
              company_business_email: provider.business_support_email,
              company_technical_email: provider.technical_support_email,
              provider_id: provider.id,
              provider_short_id: provider.short_id,
              provider_industry: provider.industry,
              provider_short_description: provider.short_description,
              provider_website: provider.website,
              provider_type: provider.accessibility
            }
          })
          this.provider_info = response
          if (this.provider_info.length === 0) {
            this.error404occured = true
            this.$store.dispatch('app/setLoading', false)
          } else {
            this.api_info[0]['display_name'] = this.provider_info[0].display_name
            this.$store.dispatch('app/setShowUpgradedPortal', (this.provider_info[0]?.theme_code.toUpperCase() === 'VIP' || this.provider_info[0]?.theme_code.toUpperCase() === 'VVIP') && this.getEditableInfo('enabled_function', 'branded_mode', false))
            this.$store.dispatch('branded/setProviderInfo', {
              verification_status: this.provider_info[0].verification_status
            })
          }
        })
        .catch(error => {
          console.log('getProviderInfo error', error)
          this.error404occured = true
          this.$store.dispatch('app/setLoading', false)
        })
    },
    async getReadme() {
      await getReadme(this.api_info[0].readme)
        .then(res => {
          this.guides = res['data']
        })
        .catch(error => {
          console.log('getAPIReadme error', error)
        })
    },
    getCategoryName(category) {
      return this.$t(getCategoryCode(category))
    },
    getCategoryFormattedName(category) {
      return getCategoryFormattedName(category)
    },
    getSwaggerDownloadLink() {
      let swaggerJson = null
      if (this.api_info && this.api_info[0]?.swagger_file) {
        if (this.api_info[0].swagger_file.charAt(0) === '"') {
          this.api_info[0].swagger_file = this.api_info[0].swagger_file.substring(1, this.api_info[0].swagger_file.length - 1)
        }
        try {
          swaggerJson = JSON.parse(this.api_info[0].swagger_file.replace(/\\"/g, '\"').replace(/\\\\/g, '\\'))
        } catch (err) {
          swaggerJson = JSON.parse(this.api_info[0].swagger_file)
        }
      }
      const downloadLink = window.URL.createObjectURL(new Blob([JSON.stringify(swaggerJson, null, 3)], { type: 'application/json' }))
      return downloadLink
    },
    getEditableInfo(type, id, withLangCode = true) {
      return getEditableInfo(type, id, withLangCode ? this.$i18n.locale : '')
    }
  }
}
</script>

<style lang="scss" scoped>
.disabled {
  pointer-events:none;
  color: #D3D3D3;
}
.avatar {
  width: 100px;
  height: 100px;
  box-shadow: 0 12px 15px 0 rgba(140,152,164,0.1);
}
.avatar-img {
  width: 100%;
  height: 100%;
}
.nav-item {
  margin-bottom: 2px;
  .nav-link {
    border-radius: 0px !important;
  }
}
.category, .api-group {
  padding: 0.275rem 0.75rem !important;
  margin-right: 12px;
}
.btn-disabled {
  pointer-events:none;
}
@media (max-width: 400px) {
  .nav-link.cursor-pointer {
    padding: 4px;
  }
}

.hs-nav-scroller-horizontal {
  position: relative;
}
.hs-nav-scroller-horizontal .nav {
  overflow-x: auto;
  overflow-y: hidden;
  -ms-flex-wrap: nowrap;
  flex-wrap: nowrap;
  white-space: nowrap;
  scroll-behavior: smooth;
}
.hs-nav-scroller-horizontal .nav .nav-item {
  white-space: nowrap;
}
.hs-nav-scroller-horizontal .nav .nav-link {
  white-space: nowrap;
}
.hs-nav-scroller-horizontal .nav::-webkit-scrollbar {
  display: none;
}
.hs-nav-scroller-horizontal .hs-nav-scroller-arrow-next, .hs-nav-scroller-horizontal .hs-nav-scroller-arrow-prev {
  position: absolute;
  height: 100%;
  z-index: 1;
  font-size: 1.5rem;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-align: center;
  align-items: center;
}
.hs-nav-scroller-horizontal .hs-nav-scroller-arrow-link {
  width: 3.125rem;
  color: #677788;
}
.hs-nav-scroller-horizontal .hs-nav-scroller-arrow-link:hover {
  color: #57abf0;
}
.hs-nav-scroller-horizontal .hs-nav-scroller-arrow-prev {
  left: 0;
  margin-left: -.125rem;
  background-image: linear-gradient(to right, #fff 50%, rgba(255, 255, 255, 0) 100%);
}
.hs-nav-scroller-horizontal .hs-nav-scroller-arrow-prev .hs-nav-scroller-arrow-link {
  padding: .5rem 0;
}
.hs-nav-scroller-horizontal .hs-nav-scroller-arrow-next {
  right: 0;
  margin-right: -.125rem;
  background-image: linear-gradient(to right, rgba(255, 255, 255, 0) 0, #fff 50%);
}
.hs-nav-scroller-horizontal .hs-nav-scroller-arrow-next .hs-nav-scroller-arrow-link {
  padding: .5rem 0;
  text-align: right;
}

.hs-nav-scroller-horizontal .nav-tabs .nav-link.active {
  border: 0;
  font-weight: 600;
  background-color: transparent;
}

.hs-nav-scroller-horizontal .nav-tabs .nav-link {
  border: 0;
  padding: 0.75rem 1rem;
  border-radius: 0;
}

.hs-nav-scroller-horizontal .nav-tabs .nav-link.active {
  border-bottom: #57abf0 3px solid !important;
}

@media (min-width: 992px) {
  .theme-button-mobile, .theme-outline-button-mobile {
    display: none;
  }
}
@media (max-width: 992px) {
  .theme-button-desktop, .theme-outline-button-desktop {
    display: none;
  }
}
.unlisted-info-box {
  margin: 0.75rem 1rem;
}
.invisible {
  visibility: hidden;
}
</style>
